import { makeAutoObservable } from "mobx";
import { getUrl, getWindow, stripPreviewQueryParams } from "../lib/window";
import { EventEmitter } from "@/lib/events/eventEmitter";

export const PageInfoNamespace = "pageInfo" as const;

export type PageInfoEvents = {
  urlChanged: { url: string };
};

export class PageInfo {
  _url: string = "";
  events = new EventEmitter<typeof PageInfoNamespace, PageInfoEvents>(PageInfoNamespace);
  observer!: MutationObserver;

  constructor() {
    makeAutoObservable(this);

    // Initialize URL
    this.updateUrl(getUrl());

    this.setupMutationObserver();
    this.listenEvents();
    this.interceptHistoryMethods();
  }

  private setupMutationObserver() {
    this.observer = new MutationObserver(() => {
      this.handleUrlChange();
    });
    this.observer.observe(getWindow().document.body, { childList: true, subtree: true });
  }

  private listenEvents() {
    getWindow().addEventListener("popstate", () => {
      this.handleUrlChange();
    });
    getWindow().addEventListener("hashchange", () => {
      this.handleUrlChange.bind(this);
    });
  }

  private interceptHistoryMethods() {
    const originalPushState = getWindow().history.pushState;
    const originalReplaceState = getWindow().history.replaceState;
    const handleUrlChange = this.handleUrlChange.bind(this);

    getWindow().history.pushState = function (...args: [any, string, (string | URL | null | undefined)?]) {
      originalPushState.apply(this, args);
      handleUrlChange();
    };

    getWindow().history.replaceState = function (...args: [any, string, (string | URL | null | undefined)?]) {
      originalReplaceState.apply(this, args);
      handleUrlChange();
    };
  }

  private handleUrlChange = () => {
    try {
      const newUrl = getUrl();
      this.updateUrl(newUrl);
    } catch (e) {
      console.error("Error updating URL", { e });
    }
  };

  updateUrl(url: string) {
    const currentUrl = this.url;
    this._url = url;
    if (currentUrl !== this.url) {
      this.events.emit("urlChanged", { url: this.url }, { skipGlobal: true });
    }
  }

  stop() {
    this.observer.disconnect();
    getWindow().removeEventListener("popstate", this.handleUrlChange);
    getWindow().removeEventListener("hashchange", this.handleUrlChange);
  }

  get url() {
    return stripPreviewQueryParams(this._url);
  }

  get originalUrl() {
    return this._url;
  }
}

export const pageInfo = new PageInfo();
